import { Button } from '@parrot-software/design-system';
import { LoaderCircle } from '@parrot-software/design-system/icons';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { Trans, useTranslation } from 'react-i18next';

import type { VerificationChannel } from '@identity/domain/types';
import { ChannelAwaitingCard } from '@identity/presentation/components/base';
import { IS_SMS_CHANNEL_ENABLED } from '@identity/presentation/constants';
import { useRequirementVerificationPolling, useVerificationLinkSender } from '@identity/presentation/hooks';
import { translateIdentityError } from '@identity/presentation/utils';

/** What a resend hands back: the channel it went through, and the verification it opened. */
export type PhoneResendResult = {
  channel: VerificationChannel;
  verificationUuid?: string;
};

export type PhoneAwaitingViewProps = {
  channel: VerificationChannel;
  /** Called when a resend succeeds, with the channel used and the verification it opened. */
  onResent: (result: PhoneResendResult) => void;
  /** Called when a later send replaced the verification being watched. */
  onSuperseded?: () => void;
  /** Returns to the capture view with an empty input. */
  onUseAnotherNumber: () => void;
  /** The backend now reports the number as verified. The caller refreshes the data. */
  onVerified: () => void;
  /** The number the user just typed, in E.164. */
  phone: string;
  /**
   * The verification this wait watches. Without it the wait resolves against the account, which is
   * enough for a number that was never verified.
   */
  verificationUuid?: string;
};

/**
 * Waiting view of the phone step.
 *
 * The number is shown in full: the user typed it seconds ago, so hiding digits protects nothing and
 * takes away what they need to spot the most common problem here, a wrong digit.
 *
 * The card is a shared shell; what lives here is the wiring — which requirement is waited for, what
 * a resend does, and the copy of this channel.
 *
 * @param {PhoneAwaitingViewProps} props - Component properties
 */
export const PhoneAwaitingView = ({
  channel,
  onResent,
  onSuperseded,
  onUseAnotherNumber,
  onVerified,
  phone,
  verificationUuid,
}: PhoneAwaitingViewProps) => {
  const { t } = useTranslation('identity');
  const { errorKey, errorRetryAfterSeconds, isSending, send } = useVerificationLinkSender();
  const { hasPersistentNetworkError, isTimedOut, restart } = useRequirementVerificationPolling({
    onSuperseded,
    onVerified,
    requirement: 'PHONE',
    resetKey: channel,
    verificationUuid,
  });

  const resendThrough = async (nextChannel: VerificationChannel) => {
    const result = await send({ channel: nextChannel, value: phone, isResend: true });

    if (!result) return;

    // A resend invalidates the previous link, so the whole wait starts again. It cannot go through
    // the reset key, because resending to the same channel must reset it too. A wait that names a
    // verification restarts on its own, because the new uuid is a new wait.
    restart();
    onResent({ channel: nextChannel, verificationUuid: result.verificationUuid });
  };

  return (
    <ChannelAwaitingCard
      description={
        isTimedOut ? (
          t('phone.awaiting.timedOutBody')
        ) : (
          <Trans
            components={{ strong: <strong /> }}
            i18nKey="identity:phone.awaiting.body"
            values={{ phone: formatPhoneNumberIntl(phone) || phone }}
          />
        )
      }
      errorMessage={translateIdentityError(t, errorKey, errorRetryAfterSeconds)}
      hasPersistentNetworkError={hasPersistentNetworkError}
      isTimedOut={isTimedOut}
      networkNotice={t('phone.awaiting.networkNotice')}
      title={t(channel === 'SMS' ? 'phone.awaiting.titleSms' : 'phone.awaiting.titleWhatsApp')}
    >
      <Button
        className="w-full sm:w-auto sm:px-[15px]"
        disabled={isSending}
        onClick={() => resendThrough(channel)}
        variant={isTimedOut ? 'default' : 'outline'}
      >
        {isSending && (
          <LoaderCircle aria-hidden className="size-4 shrink-0 animate-spin" data-testid="phone-resend-spinner" />
        )}
        {t('phone.awaiting.resend')}
      </Button>

      {IS_SMS_CHANNEL_ENABLED && channel !== 'SMS' && (
        <Button
          className="w-full sm:w-auto sm:px-[15px]"
          disabled={isSending}
          onClick={() => resendThrough('SMS')}
          variant="secondary"
        >
          {t('phone.sendSms')}
        </Button>
      )}

      <Button className="w-full sm:w-auto sm:px-[15px]" onClick={onUseAnotherNumber} variant="link">
        {t('phone.awaiting.useAnotherNumber')}
      </Button>
    </ChannelAwaitingCard>
  );
};
